import * as React from "react";
import clsx from "clsx";
import isHostComponent from "./utils/isHostComponent";
import { composeClasses } from "../../utils";
import { getSliderUtilityClass } from "./sliderUnstyledClasses";
import SliderValueLabelUnstyled from "./SliderValueLabelUnstyled";
import useSlider, { valueToPercent } from "./useSlider";
import useSlotProps from "./hooks/useSlotProps";
import type {
    SliderUnstyledOwnerState,
    SliderUnstyledProps,
} from "./SliderUnstyled.types";
import type { SliderUnstyledClasses } from "./sliderUnstyledClasses";

const Identity = (x: number): number => x;

interface UseUtilityClassesOwnerState {
    disabled?: boolean;
    dragging?: boolean;
    marked?: boolean;
    orientation?: "horizontal" | "vertical";
    track?: "normal" | false | "inverted";
    classes?: Partial<SliderUnstyledClasses>;
}

type SlotsInput = Record<string, ReadonlyArray<string | false | undefined | null>>;

const useUtilityClasses = (ownerState: UseUtilityClassesOwnerState) => {
    const { disabled, dragging, marked, orientation, track, classes } = ownerState;

    const slots: SlotsInput = {
        root: [
            "root",
            disabled && "disabled",
            dragging && "dragging",
            marked && "marked",
            orientation === "vertical" && "vertical",
            track === "inverted" && "trackInverted",
            track === false && "trackFalse",
        ],
        rail: ["rail"],
        track: ["track"],
        mark: ["mark"],
        markActive: ["markActive"],
        markLabel: ["markLabel"],
        markLabelActive: ["markLabelActive"],
        valueLabel: ["valueLabel"],
        thumb: ["thumb", disabled && "disabled"],
        active: ["active"],
        disabled: ["disabled"],
        focusVisible: ["focusVisible"],
    };

    return composeClasses(slots, getSliderUtilityClass, classes);
};

interface ForwardProps {
    children: React.ReactNode;
}

const Forward = ({ children }: ForwardProps): React.ReactElement => <>{children}</>;

const SliderUnstyled = React.forwardRef<HTMLSpanElement, SliderUnstyledProps>(
    function SliderUnstyled(props, ref) {
        const {
            "aria-label": ariaLabel,
            "aria-valuetext": ariaValuetext,
            className,
            component,
            classes: classesProp,
            disableSwap = false,
            disabled = false,
            getAriaLabel,
            getAriaValueText,
            marks: marksProp = false,
            max = 100,
            min = 0,
            name,
            onChange,
            onChangeCommitted,
            orientation = "horizontal",
            scale = Identity,
            step = 1,
            tabIndex,
            track = "normal",
            value: valueProp,
            valueLabelDisplay = "off",
            valueLabelFormat = Identity,
            isRtl = false,
            components = {},
            componentsProps = {},
            ...other
        } = props;

        const ownerState: SliderUnstyledOwnerState = {
            ...props,
            marks: marksProp,
            classes: classesProp,
            disabled,
            focusedThumbIndex: -1,
            isRtl,
            mark: marksProp,
            max,
            min,
            orientation,
            scale,
            step,
            track,
            valueLabelDisplay,
            valueLabelFormat,
        };

        const {
            axisProps,
            getRootProps,
            getHiddenInputProps,
            getThumbProps,
            open,
            active,
            axis,
            range,
            focusedThumbIndex,
            dragging,
            marks,
            values,
            trackOffset,
            trackLeap,
        } = useSlider({ ...ownerState, ref });

        ownerState.marked = marks.length > 0 && marks.some((mark) => mark.label);
        ownerState.dragging = dragging;
        ownerState.focusedThumbIndex = focusedThumbIndex;

        const classes = useUtilityClasses(ownerState);

        const Root = component ?? components.Root ?? "span";
        const rootProps = useSlotProps({
            elementType: Root,
            getSlotProps: getRootProps,
            externalSlotProps: componentsProps.root,
            externalForwardedProps: other,
            ownerState,
            className: [classes.root, className],
        });

        const Rail = components.Rail ?? "span";
        const railProps = useSlotProps({
            elementType: Rail,
            externalSlotProps: componentsProps.rail,
            ownerState,
            className: classes.rail,
        });

        const Track = components.Track ?? "span";
        const trackProps = useSlotProps({
            elementType: Track,
            externalSlotProps: componentsProps.track,
            additionalProps: {
                style: {
                    ...axisProps[axis].offset(trackOffset),
                    ...axisProps[axis].leap(trackLeap),
                },
            },
            ownerState,
            className: classes.track,
        });

        const Thumb = components.Thumb ?? "span";
        const thumbProps = useSlotProps({
            elementType: Thumb,
            getSlotProps: getThumbProps,
            externalSlotProps: componentsProps.thumb,
            ownerState,
        });

        const ValueLabel = components.ValueLabel ?? SliderValueLabelUnstyled;
        const valueLabelProps = useSlotProps({
            elementType: ValueLabel,
            externalSlotProps: componentsProps.valueLabel,
            ownerState,
        });

        const Mark = components.Mark ?? "span";
        const markProps = useSlotProps({
            elementType: Mark,
            externalSlotProps: componentsProps.mark,
            ownerState,
            className: classes.mark,
        });

        const MarkLabel = components.MarkLabel ?? "span";
        const markLabelProps = useSlotProps({
            elementType: MarkLabel,
            externalSlotProps: componentsProps.markLabel,
            ownerState,
        });

        const Input = components.Input || "input";
        const inputProps = useSlotProps({
            elementType: Input,
            getSlotProps: getHiddenInputProps,
            externalSlotProps: componentsProps.input,
            ownerState,
        });

        return (
            <Root {...rootProps}>
                <Rail {...railProps} />
                <Track {...trackProps} />
                {marks
                    .filter((mark) => mark.value >= min && mark.value <= max)
                    .map((mark, index) => {
                        const percent = valueToPercent(mark.value, min, max);
                        const style = axisProps[axis].offset(percent);

                        let markActive: boolean;
                        if (track === false) {
                            markActive = values.indexOf(mark.value) !== -1;
                        } else {
                            markActive =
                                (track === "normal" &&
                                    (range
                                        ? mark.value >= values[0] &&
                                          mark.value <= values[values.length - 1]
                                        : mark.value <= values[0])) ||
                                (track === "inverted" &&
                                    (range
                                        ? mark.value <= values[0] ||
                                          mark.value >= values[values.length - 1]
                                        : mark.value >= values[0]));
                        }

                        return (
                            <React.Fragment key={mark.value}>
                                <Mark
                                    data-index={index}
                                    {...markProps}
                                    {...(!isHostComponent(Mark) && {
                                        markActive,
                                    })}
                                    style={{ ...style, ...markProps.style }}
                                    className={clsx(markProps.className, {
                                        [classes.markActive]: markActive,
                                    })}
                                />
                                {mark.label != null ? (
                                    <MarkLabel
                                        aria-hidden
                                        data-index={index}
                                        {...markLabelProps}
                                        {...(!isHostComponent(MarkLabel) && {
                                            markLabelActive: markActive,
                                        })}
                                        style={{ ...style, ...markLabelProps.style }}
                                        className={clsx(classes.markLabel, markLabelProps.className, {
                                            [classes.markLabelActive]: markActive,
                                        })}
                                    >
                                        {mark.label}
                                    </MarkLabel>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                {values.map((value, index) => {
                    const percent = valueToPercent(value, min, max);
                    const style = axisProps[axis].offset(percent);

                    const ValueLabelComponent =
                        valueLabelDisplay === "off" ? Forward : ValueLabel;

                    return (
                        <React.Fragment key={index}>
                            <ValueLabelComponent
                                {...(!isHostComponent(ValueLabelComponent) && {
                                    valueLabelFormat,
                                    valueLabelDisplay,
                                    value:
                                        typeof valueLabelFormat === "function"
                                            ? valueLabelFormat(scale(value), index)
                                            : valueLabelFormat,
                                    index,
                                    open:
                                        open === index ||
                                        active === index ||
                                        valueLabelDisplay === "on",
                                    disabled,
                                })}
                                {...valueLabelProps}
                                className={clsx(classes.valueLabel, valueLabelProps.className)}
                            >
                                <Thumb
                                    data-index={index}
                                    data-focusvisible={focusedThumbIndex === index}
                                    {...thumbProps}
                                    className={clsx(classes.thumb, thumbProps.className, {
                                        [classes.active]: active === index,
                                        [classes.focusVisible]: focusedThumbIndex === index,
                                    })}
                                    style={{
                                        ...style,
                                        pointerEvents:
                                            disableSwap && active !== -1 && active !== index
                                                ? "none"
                                                : undefined,
                                        ...thumbProps.style,
                                    }}
                                >
                                    <Input
                                        data-index={index}
                                        aria-label={
                                            getAriaLabel ? getAriaLabel(index) : ariaLabel
                                        }
                                        aria-valuenow={scale(value)}
                                        aria-valuetext={
                                            getAriaValueText
                                                ? getAriaValueText(scale(value), index)
                                                : ariaValuetext
                                        }
                                        value={values[index]}
                                        {...inputProps}
                                    />
                                </Thumb>
                            </ValueLabelComponent>
                        </React.Fragment>
                    );
                })}
            </Root>
        );
    },
);

export default SliderUnstyled;
